import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import type { CompanyFactorySummary } from '@indutrust/factory-model';
import { EnvironmentService } from '@indutrust/shared-env';
import { BaseApiClient } from '@indutrust/shared-model';

/**
 * API client for the company-scoped factories collection
 * (`GET /companies/{companyId}/factories`).
 *
 * The collection endpoint returns the lightweight
 * {@link CompanyFactorySummary} projection — just enough fields to
 * render a card row and link out to the editor. Fetching the full
 * editable shape ({@link import('@indutrust/factory-model').FactoryEdit})
 * is the {@link import('../factory/factory-api-client.service').FactoryApiClient}
 * responsibility (`GET /factories/:id`).
 */
@Service()
export class CompanyFactoryApiClient extends BaseApiClient<CompanyFactorySummary> {
  protected envService = inject(EnvironmentService);
  protected http = inject(HttpClient);

  protected template: Partial<CompanyFactorySummary> = {};

  /**
   * Creates a new factory with the given name.
   *
   * The underlying `POST /factories` endpoint actually returns the full
   * {@link import('@indutrust/factory-model').FactoryEdit}, but the
   * caller (the company-factories store) only needs the fields exposed
   * by {@link CompanyFactorySummary} — the supplier is immediately
   * navigated to `/factory/:id/edit` where the full record is loaded
   * via the dedicated detail endpoint.
   *
   * @param name - The name of the factory to create.
   * @returns An observable that emits the newly created factory summary.
   */
  createFactory(name: string) {
    const url = `${this.envService.getApiUrl()}/factories`;

    // Pending: remove website and creation_date from the request body.
    return this.http.post<CompanyFactorySummary>(url, {
      name: name,
      website: 'indutrust.io',
      creation_date: '2025',
    });
  }

  get url(): string {
    return `${this.envService.getApiUrl()}/companies/{companyId}/factories`;
  }
}
